import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
  Validators,
} from '@angular/forms';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { finalize, Observable, of, Subject, take, takeUntil } from 'rxjs';

import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { SvgIconComponent } from 'angular-svg-icon';

import { DRAWER_IDS } from '../../../core/constants/drawer-ids.constant';
import { DrawerService } from '../../../core/services/drawer.service';
import { LoaderService } from '../../../core/services/loader.service';
import { CommonService } from '../../../shared/services/common.service';
import { AuthService } from '../../../core/services/auth.service';
import { OrganizationSelectionService } from '../../../core/services/organization-selection.service';
import { ErrorResponse } from '../../../core/models/error.models';
import { LookupItem } from '../../../core/models/lookup.models';
import { TemplateMode } from '../bip-template.enums';
import {
  BehaviorQuestionnaires,
  editDrawerParams,
  TemplateModel,
} from '../bip-template.interface';
import { BipTemplateService } from '../bip-template.service';
import { CustomQuestionService } from '../../manage-settings/custom-question/custom-question.service';
import {
  PlanPayload,
  QuestionItem,
} from '../../manage-settings/manage-settings.interface';
import { userDetailModel } from '../../organizations/organization.interface';
import { OrganizationSearchComponent } from '../../../components/organization-search/organization-search.component';
import { ManageSettingsService } from '../../manage-settings/manage-settings.service';
interface DataItem {
  row: string;
}
@Component({
  selector: 'app-template-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DragDropModule,
    NzSpinModule,
    NzCheckboxModule,
    NzSwitchModule,
    NzTableModule,
    NzButtonModule,
    NzFormModule,
    NzFormControlComponent,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzInputModule,
    CdkDropList,
    CdkDrag,
    SvgIconComponent,
    OrganizationSearchComponent,
  ],
  templateUrl: './template-form.component.html',
  styleUrl: './template-form.component.scss',
})
export class TemplateFormComponent implements OnDestroy, OnInit {
  private readonly destroy$ = new Subject<void>();

  templateForm!: FormGroup;
  loading$: Observable<boolean> = of(false);
  errorMessage = '';
  drawerParams: editDrawerParams = {
    selectedTemplate: null,
    mode: TemplateMode.EDIT,
    organizationId: 0,
  };

  orgId: number | null = 0;
  templateData: TemplateModel | null = null;
  userData: userDetailModel | null = null;
  customQuestions: QuestionItem[] = [];
  orginialCustomQuestions: QuestionItem[] = [];
  behvaiorSections: BehaviorQuestionnaires[] = [];
  originalBehaviorSections: LookupItem[] = [];

  readonly fieldData = [
    { label: 'Student Age', field: 'Age' },
    { label: 'Student School', field: 'School Name' },
    { label: 'Behavior Screener', field: 'Screener Date' },
    { label: 'Plan Created', field: 'Created Date' },
    { label: 'Last Revised', field: 'Revised Date' },
    { label: 'Safety Plan', field: 'Safety Plan' },
  ] as const;

  readonly sections = [
    { title: 'Challenging Behavior', type: 'switch' },
    { title: 'Strategy Details', type: 'switch' },
    { title: 'Custom Questions', type: 'custom' },
    { title: 'Reports', type: 'switch' },
  ] as const;
  listOfTableRow: DataItem[] = [
    {
      row: '',
    },
  ];
  planList: PlanPayload[] = [];

  constructor(
    private readonly drawerService: DrawerService,
    private readonly loaderService: LoaderService,
    private readonly bipTemplateService: BipTemplateService,
    private readonly commonService: CommonService,
    private readonly fb: FormBuilder,
    private readonly customQuestionService: CustomQuestionService,
    private readonly authService: AuthService,
    private readonly orgSelection: OrganizationSelectionService,
    private readonly cdr: ChangeDetectorRef,
    private readonly manageSettingsService: ManageSettingsService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.initializeComponent();
  }

  ngOnInit(): void {
    this.initForm();

    if (this.isEditMode && this.drawerParams.selectedTemplate) {
      this.loadTemplateData();
    } else {
      this.initializeNewTemplate();
      this.getAllPlans();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeComponent(): void {
    this.getBehaviorSections();
    this.setupDrawerParams();
    this.subscribeToUserData();
  }

  private loadTemplateData(): void {
    setTimeout(() => {
      if (this.drawerParams.selectedTemplate) {
        this.loadTemplateById(this.drawerParams.selectedTemplate);
      }
    });
  }

  private initializeNewTemplate(): void {
    setTimeout(() => {
      this.setFormValue();
      this.getCustomQuestionBYOrgId();
    });
  }

  private subscribeToUserData(): void {
    this.authService.userData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(userData => {
        this.userData = userData;
      });
  }

  get isEditMode(): boolean {
    return this.drawerParams.mode === TemplateMode.EDIT;
  }

  get isTemplateSectionValid(): boolean {
    return this.hasSelectedControl(this.templateSections);
  }

  get isTemplateFieldsValid(): boolean {
    return this.hasSelectedControl(this.templateFields);
  }

  get isCustomQuestionsSelected(): boolean {
    return this.templateSections.controls.some(
      ctrl =>
        ctrl.value.sectionName === 'Custom Questions' && ctrl.value.isSelected
    );
  }

  get isBehvaiorQuestionnairesValid(): boolean {
    return this.hasSelectedControl(this.behaviorQuestionnaires);
  }

  get isBehaviorSectionSelected(): boolean {
    const challengingBehaviorSection = this.templateSections.controls.find(
      ctrl =>
        ctrl.value.sectionName === 'Challenging Behavior' &&
        ctrl.value.isSelected
    );

    if (challengingBehaviorSection) {
      return this.behvaiorSections.some(section => section.isSelected);
    }

    return true;
  }

  get isTemplateQuestionsValid(): boolean {
    return (
      !this.isCustomQuestionsSelected ||
      (this.templateQuestions?.length ?? 0) > 0
    );
  }

  get isFormValid(): boolean {
    const templateNameValid =
      this.templateForm.get('templateName')?.valid ?? false;

    return (
      templateNameValid &&
      this.isTemplateSectionValid &&
      this.isTemplateFieldsValid &&
      this.isTemplateQuestionsValid &&
      this.isBehaviorSectionSelected
    );
  }

  get templateFields(): FormArray {
    return this.templateForm.get('templateFields') as FormArray;
  }

  get templateFieldsControls(): FormGroup[] {
    return this.templateFields?.controls as FormGroup[];
  }

  get templateSections(): FormArray {
    return this.templateForm.get('templateSections') as FormArray;
  }

  get templateQuestions(): number[] {
    return this.templateForm.value.templateQuestionIds;
  }

  get behaviorQuestionnaires(): FormArray {
    return this.templateForm.get('behaviorQuestionnaires') as FormArray;
  }

  get planSections(): FormArray {
    return this.templateForm.get('planSections') as FormArray;
  }

  private hasSelectedControl(formArray: FormArray): boolean {
    return formArray.controls.some(ctrl => ctrl.value.isSelected);
  }

  private initForm(): void {
    this.templateForm = this.fb.group({
      templateName: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(250),
        ],
      ],
      templateFields: this.fb.array([]),
      templateSections: this.fb.array([]),
      planSections: this.fb.array([]),
      templateQuestionIds: this.fb.control<number[]>([]),
      behaviorQuestionnaires: this.fb.array([]),
      isDefault: [false],
      isIncludeStrategyGuidelines: [false],
      isPublished: [false],
      organizationId: this.orgId,
    });
  }

  getBehaviorSections(): void {
    this.loaderService.show();
    this.commonService
      .getDataByType('ChallengingBehaviorOptions', '')
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: response => {
          if (response.result) {
            this.processBehaviorSectionsResponse(response.result);
          }
        },
        error: (error: ErrorResponse) => this.handleApiError(error),
      });
  }

  orgChange(value: number | null): void {
    this.orgId = value ?? 0;

    if (this.templateForm) {
      this.templateForm.patchValue({ organizationId: this.orgId });
    }

    setTimeout(() => {
      this.getCustomQuestionBYOrgId();
    });
  }

  private processBehaviorSectionsResponse(result: LookupItem[]): void {
    this.behvaiorSections = result.map((item: LookupItem, index: number) => ({
      sectionId: item.id,
      questionId: 0,
      isSelected: false,
      behaviorTypeSequenceNumber: index + 1,
      questionSequenceNumber: 0,
      applicationObjectId: item.id,
      value: item.value,
    }));
    this.originalBehaviorSections = result;
  }

  private handleApiError(error: ErrorResponse): void {
    const errorMessages = error.errors?.map(
      err => err.description || err.detailedMessage
    ) || [error.message || 'An error occurred'];
    this.commonService.notify('error', errorMessages);
  }

  private setupDrawerParams(): void {
    this.drawerService
      .getParams<editDrawerParams>(DRAWER_IDS.ADD_TEMPLATE)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.drawerParams = params;

          if (params.organizationId && params.organizationId > 0) {
            this.orgId = params.organizationId;
            if (this.templateForm) {
              this.templateForm.patchValue({ organizationId: this.orgId });
            }
            setTimeout(() => {
              this.getCustomQuestionBYOrgId();
            });
          }
        }
      });
  }

  loadTemplateById(templatedId: number) {
    this.loaderService.show();
    this.bipTemplateService
      .getTemplateById(templatedId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: response => {
          if (response.result) {
            this.templateData = response.result;
            if (
              this.drawerParams.organizationId &&
              this.drawerParams.organizationId > 0
            ) {
              this.orgId = this.drawerParams.organizationId;
            }
            this.templateForm.reset();
            setTimeout(() => {
              this.setFormValue();
              this.getCustomQuestionBYOrgId();
              this.getCustomQuestionsByTemplateId(templatedId);
              this.getAllPlans();
            });
          }
        },
        error: ({ errors, message }: ErrorResponse) => {
          this.loaderService.hide();
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  setBehaviorQuestionnaires(): void {
    if (!this.templateData?.behaviorQuestionaries?.length) {
      return;
    }

    this.updateTemplateQuestionIds();
    const processedSections = this.processTemplateBehaviorData();
    this.finalizeBehaviorSectionsSetup(processedSections);
  }

  getCustomQuestionBYOrgId(): void {
    if (!this.orgId || this.orgId <= 0) {
      return;
    }

    this.loaderService.show();
    this.customQuestionService
      .getQuestionsByOrg(this.orgId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: response => {
          if (response.result) {
            this.orginialCustomQuestions = response.result;
            this.customQuestions = response.result;
            if (this.templateData?.behaviorQuestionaries?.length) {
              setTimeout(() => {
                this.setBehaviorQuestionnaires();
              });
            }
          }
        },
        error: (error: ErrorResponse) => this.handleApiError(error),
      });
  }

  private updateTemplateQuestionIds(): void {
    const behaviorQuestionIds = this.templateData!.behaviorQuestionaries.filter(
      item => item.questionId && item.questionId !== 0
    )
      .map(item => item.questionId!)
      .filter((id): id is number => id !== undefined);

    const templateQuestionIds =
      this.templateForm.value.templateQuestionIds || [];
    const newIds = behaviorQuestionIds.filter(
      id => !templateQuestionIds.includes(id)
    );

    if (newIds.length > 0) {
      this.templateForm.patchValue({
        templateQuestionIds: [...templateQuestionIds, ...newIds],
      });
    }
  }

  private processTemplateBehaviorData(): BehaviorQuestionnaires[] {
    const existingSections = this.getExistingSections();
    const sectionQuestionMap = this.buildSectionQuestionMap();

    return this.buildProcessedSections(existingSections, sectionQuestionMap);
  }

  private getExistingSections(): Set<number> {
    const existingSections = new Set<number>();

    this.templateData!.behaviorQuestionaries.forEach(item => {
      if (item.questionId === 0) {
        existingSections.add(item.applicationObjectId || 0);
      }
    });

    return existingSections;
  }

  private buildSectionQuestionMap(): Map<number, boolean> {
    const sectionQuestionMap = new Map<number, boolean>();

    this.templateData!.behaviorQuestionaries.forEach(item => {
      if (item.questionId !== 0) {
        sectionQuestionMap.set(item.applicationObjectId || 0, item.isSelected);
      }
    });

    return sectionQuestionMap;
  }

  private buildProcessedSections(
    existingSections: Set<number>,
    sectionQuestionMap: Map<number, boolean>
  ): BehaviorQuestionnaires[] {
    const processedSections: BehaviorQuestionnaires[] = [];

    this.templateData!.behaviorQuestionaries.forEach(item => {
      const applicationObjectId = item.applicationObjectId || 0;

      this.addMissingSectionHeader(
        item,
        existingSections,
        processedSections,
        applicationObjectId
      );

      const behaviorSection = this.createBehaviorSection(
        item,
        applicationObjectId,
        sectionQuestionMap
      );

      processedSections.push(behaviorSection);
    });

    this.markSectionHeadersAsSelected(processedSections, sectionQuestionMap);

    return processedSections.sort(
      (a, b) => a.behaviorTypeSequenceNumber - b.behaviorTypeSequenceNumber
    );
  }

  private addMissingSectionHeader(
    item: BehaviorQuestionnaires,
    existingSections: Set<number>,
    processedSections: BehaviorQuestionnaires[],
    applicationObjectId: number
  ): void {
    if (item.questionId !== 0 && !existingSections.has(applicationObjectId)) {
      const sectionInfo = this.originalBehaviorSections.find(
        section => section.id === applicationObjectId
      );

      processedSections.push({
        sectionId: applicationObjectId,
        questionId: 0,
        isSelected: true,
        behaviorTypeSequenceNumber: item.behaviorTypeSequenceNumber,
        questionSequenceNumber: 0,
        applicationObjectId: applicationObjectId,
        value: sectionInfo?.value || item.value || '',
      });

      existingSections.add(applicationObjectId);
    }
  }

  private createBehaviorSection(
    item: BehaviorQuestionnaires,
    applicationObjectId: number,
    sectionQuestionMap: Map<number, boolean>
  ): BehaviorQuestionnaires {
    const sectionInfo = this.originalBehaviorSections.find(
      section => section.id === applicationObjectId
    );

    const isSelectedValue =
      item.questionId === 0 && sectionQuestionMap.has(applicationObjectId)
        ? true
        : item.isSelected;

    return {
      sectionId: applicationObjectId,
      questionId: item.questionId || 0,
      isSelected: isSelectedValue,
      behaviorTypeSequenceNumber: item.behaviorTypeSequenceNumber,
      questionSequenceNumber: item.questionSequenceNumber || 0,
      applicationObjectId: applicationObjectId,
      value:
        item.questionId !== 0
          ? this.orginialCustomQuestions.find(q => q.id === item.questionId)
              ?.customQuestion ||
            item.value ||
            ''
          : sectionInfo?.value || item.value || '',
    };
  }

  private markSectionHeadersAsSelected(
    processedSections: BehaviorQuestionnaires[],
    sectionQuestionMap: Map<number, boolean>
  ): void {
    processedSections.forEach(item => {
      if (item.questionId === 0 && sectionQuestionMap.has(item.sectionId)) {
        item.isSelected = true;
      }
    });
  }

  private finalizeBehaviorSectionsSetup(
    processedSections: BehaviorQuestionnaires[]
  ): void {
    this.behvaiorSections = processedSections;

    const behaviorQuestionIds = this.templateData!.behaviorQuestionaries.filter(
      item => item.questionId && item.questionId !== 0
    )
      .map(item => item.questionId!)
      .filter((id): id is number => id !== undefined);

    this.customQuestions = this.customQuestions.filter(
      question => !behaviorQuestionIds.includes(question.id)
    );

    setTimeout(() => {
      this.updateBehaviorQuestionnaires();
      this.cdr.markForCheck();
    });
  }

  getCustomQuestionsByTemplateId(templateId: number): void {
    this.loaderService.show();
    this.customQuestionService
      .getQuestionsByTemplateId(templateId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.loaderService.hide();
          if (response.result) {
            const resultQuestionIds = new Set(response.result.map(q => q.id));

            const behaviorQuestionIds = new Set(
              this.behvaiorSections
                .filter(
                  section => section.questionId && section.questionId !== 0
                )
                .map(section => section.questionId!)
            );

            const resultQuestions = response.result
              .filter(question => !behaviorQuestionIds.has(question.id))
              .map(question => ({ ...question, isSelected: true }));

            const remainingCustomQuestions = this.customQuestions.filter(
              question =>
                !resultQuestionIds.has(question.id) &&
                !behaviorQuestionIds.has(question.id)
            );

            this.customQuestions = [
              ...resultQuestions,
              ...remainingCustomQuestions,
            ];
          }
        },
        error: error => {
          this.loaderService.hide();
          this.commonService.notify('error', error.message);
        },
      });
  }

  setFormValue() {
    const fieldsArray = this.templateFields;
    fieldsArray.clear();
    if (this.templateData && this.templateData.templateFields.length > 0) {
      this.templateData.templateFields.forEach(field => {
        if (field.fieldName === 'Safety Plan') return;
        fieldsArray.push(
          this.fb.group({
            id: field.id || 0,
            fieldName: field.fieldName,
            isSelected: field.isSelected,
          })
        );
      });
    } else {
      this.fieldData.forEach(field => {
        if (typeof field.label === 'string' && field.label === 'Safety Plan') {
          return;
        }
        fieldsArray.push(
          this.fb.group({
            id: 0,
            fieldName: field.label,
            isSelected: false,
          })
        );
      });
    }

    const sectionArray = this.templateSections;
    sectionArray.clear();
    const planSectionsArray = this.planSections;
    planSectionsArray.clear();

    if (this.templateData && this.templateData.templateSections.length > 0) {
      const staticSectionNames: string[] = this.sections.map(s => s.title);

      const staticSections = this.templateData.templateSections.filter(
        section => staticSectionNames.includes(section.sectionName)
      );

      const planSections = this.templateData.templateSections.filter(
        section => !staticSectionNames.includes(section.sectionName)
      );

      staticSections.forEach(section => {
        sectionArray.push(
          this.fb.group({
            id: section.id || 0,
            sectionName: section.sectionName,
            sequenceNumber: section.sequenceNumber,
            isSelected: section.isSelected,
          })
        );
      });

      planSections.forEach(section => {
        planSectionsArray.push(
          this.fb.group({
            id: section.id || 0,
            sectionName: section.sectionName,
            sequenceNumber: section.sequenceNumber,
            isSelected: section.isSelected,
          })
        );
      });
    } else {
      this.sections.forEach((section, index) => {
        sectionArray.push(
          this.fb.group({
            id: 0,
            sectionName: section.title,
            sequenceNumber: index + 1,
            isSelected: false,
          })
        );
      });
    }

    if (this.templateData) {
      this.templateForm.patchValue({
        templateName: this.templateData.templateName,
        templateQuestionIds: this.templateData.templateQuestionIds,
        isDefault: this.templateData.isDefault,
        isPublished: false,
        isIncludeStrategyGuidelines:
          this.templateData.isIncludeStrategyGuidelines,
        organizationId: this.orgId,
      });
    }
  }

  patchFormValue() {
    if (this.templateData) {
      this.templateForm.patchValue({
        templateName: this.templateData.templateName,
        templateQuestionIds: this.templateData.templateQuestionIds,
        isDefault: false,
        isPublished: false,
        organizationId: this.orgId,
      });

      const fieldsArray = this.templateFields;
      fieldsArray.clear();
      this.templateData.templateFields.forEach(field => {
        fieldsArray.push(
          this.fb.group({
            id: field.id || 0,
            fieldName: field.fieldName,
            isSelected: field.isSelected,
          })
        );
      });

      const sectionArray = this.templateSections;
      sectionArray.clear();
      const planSectionsArray = this.planSections;
      planSectionsArray.clear();

      const staticSectionNames: string[] = this.sections.map(s => s.title);

      this.templateData.templateSections.sort(
        (a, b) => a.sequenceNumber - b.sequenceNumber
      );

      const staticSections = this.templateData.templateSections.filter(
        section => staticSectionNames.includes(section.sectionName)
      );

      const planSections = this.templateData.templateSections.filter(
        section => !staticSectionNames.includes(section.sectionName)
      );

      staticSections.forEach(section => {
        sectionArray.push(
          this.fb.group({
            id: section.id || 0,
            sectionName: section.sectionName,
            sequenceNumber: section.sequenceNumber,
            isSelected: section.isSelected,
          })
        );
      });

      planSections.forEach(section => {
        planSectionsArray.push(
          this.fb.group({
            id: section.id || 0,
            sectionName: section.sectionName,
            sequenceNumber: section.sequenceNumber,
            isSelected: section.isSelected,
          })
        );
      });
    }
  }

  getQuestionsFormArray(section: AbstractControl): FormArray {
    return section.get('questions') as FormArray;
  }

  toggleSelection(index: number, checked: boolean): void {
    this.templateFields.at(index).patchValue({ isSelected: checked });
  }

  toggleSection(index: number, checked: boolean): void {
    this.templateSections.at(index).patchValue({ isSelected: checked });
  }

  drop(event: CdkDragDrop<FormGroup[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      const formArray = this.templateSections;

      if (
        event.previousIndex < 0 ||
        event.previousIndex >= formArray.length ||
        event.currentIndex < 0 ||
        event.currentIndex >= formArray.length
      ) {
        return;
      }

      const control = formArray.at(event.previousIndex);
      formArray.removeAt(event.previousIndex);
      formArray.insert(event.currentIndex, control);

      formArray.controls.forEach((control, index) => {
        control.patchValue({ sequenceNumber: index + 1 });
      });

      const staticSectionsCount = formArray.length;
      const planSectionsArray = this.planSections;
      planSectionsArray.controls.forEach((ctrl, index) => {
        ctrl.patchValue({ sequenceNumber: staticSectionsCount + index + 1 });
      });

      this.cdr.markForCheck();
    }
  }

  isBehaviorQuestionnairesSelected(sectionId: number): boolean {
    const behaviorSection = this.behvaiorSections.find(
      section => section.sectionId === sectionId
    );
    return behaviorSection ? behaviorSection.isSelected : false;
  }

  toggleQuestionnairesSelection(sectionId: number, isChecked: boolean) {
    this.behvaiorSections.forEach(item => {
      if (item.sectionId === sectionId) {
        item.isSelected = isChecked;
      }
    });

    setTimeout(() => {
      this.updateBehaviorQuestionnaires();
      this.cdr.markForCheck();
    });
  }

  toggleQuestionSelection(questionId: number, isChecked: boolean) {
    let templateQuestions = this.templateForm.value.templateQuestionIds;
    if (isChecked) {
      if (templateQuestions && !templateQuestions.includes(questionId)) {
        templateQuestions.push(questionId);
      }
    } else {
      templateQuestions = templateQuestions.filter(
        (id: number) => id !== questionId
      );
    }
    this.templateForm.patchValue({
      templateQuestionIds: templateQuestions,
    });
  }

  onCancel() {
    this.drawerService.closeDrawer(DRAWER_IDS.ADD_TEMPLATE);
  }

  submit() {
    if (!this.orgId || this.orgId <= 0) {
      this.commonService.notify(
        'error',
        'Please select a valid organization before saving.'
      );
      return;
    }
    this.templateForm.patchValue({ organizationId: this.orgId });
    const behaviorQuestionIds = this.behvaiorSections
      .filter(section => section.questionId && section.questionId !== 0)
      .map(section => section.questionId!)
      .filter((id): id is number => id !== undefined);

    const currentTemplateQuestionIds =
      this.templateForm.value.templateQuestionIds || [];
    const allQuestionIds = [
      ...new Set([...currentTemplateQuestionIds, ...behaviorQuestionIds]),
    ];

    this.templateForm.patchValue({
      templateQuestionIds: allQuestionIds,
    });

    this.loaderService.show();
    const payload = this.preparePayload();
    const operation$ =
      this.isEditMode && this.templateData?.id
        ? this.bipTemplateService.editTemplate(payload, this.templateData?.id)
        : this.bipTemplateService.createTemplate(payload);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: response => {
          if (response && response.result && !this.isEditMode) {
            this.templateData = {
              ...(this.templateData || {}),
              id: response.result.id,
              templateName: response.result.templateName,
            } as TemplateModel;
          }

          this.syncTemplateQuestionOrder();
          this.commonService.notify(
            'success',
            `Behavior Template ${this.isEditMode ? 'updated' : 'created'} successfully`
          );
          setTimeout(() => {
            this.drawerService.closeDrawer(DRAWER_IDS.ADD_TEMPLATE);
          }, 500);
        },
        error: error => {
          this.loaderService.hide();
          this.errorMessage =
            error?.message || error?.errors?.length
              ? error.errors[0].field
              : '';
          this.commonService.notify('error', this.errorMessage);
        },
      });
  }
  private preparePayload() {
    const formValue = this.templateForm.value;

    const combinedSections = [
      ...formValue.templateSections,
      ...formValue.planSections,
    ];

    const payload = {
      ...formValue,
      templateSections: combinedSections,
    };

    delete payload.planSections;

    return payload;
  }
  publish() {
    this.templateForm.patchValue({
      isPublished: true,
    });
    this.submit();
  }

  private handleError(error: ErrorResponse): void {
    this.errorMessage = error?.message;
    this.commonService.notify('error', this.errorMessage);
  }

  dropCustomQuestion(event: CdkDragDrop<QuestionItem[]>) {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(
      this.customQuestions,
      event.previousIndex,
      event.currentIndex
    );
  }

  handleCustomQuestionDrop(event: CdkDragDrop<QuestionItem[]>) {
    if (event.previousContainer === event.container) {
      this.reorderCustomQuestions(event);
    } else if (event.previousContainer.id === 'behaviorSectionsList') {
      this.moveBehaviorQuestionToCustom(
        event as CdkDragDrop<(LookupItem | QuestionItem)[]>
      );
    } else {
      this.moveCustomQuestionToBehavior(event);
    }
  }

  handleBehaviorSectionDrop(event: CdkDragDrop<(LookupItem | QuestionItem)[]>) {
    if (event.previousContainer === event.container) {
      this.reorderBehaviorSections(event);
    } else if (event.previousContainer.id === 'customQuestionsList') {
      this.receiveCustomQuestionInBehavior(event);
    } else if (event.container.id === 'customQuestionsList') {
      this.moveBehaviorQuestionToCustom(event);
    }
  }

  handlePlanDrop(event: CdkDragDrop<PlanPayload[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.planList, event.previousIndex, event.currentIndex);

      const staticSectionsCount = this.templateSections.length;
      const planSectionsArray = this.planSections;

      const control = planSectionsArray.at(event.previousIndex);
      planSectionsArray.removeAt(event.previousIndex);
      planSectionsArray.insert(event.currentIndex, control);

      planSectionsArray.controls.forEach((ctrl, index) => {
        const newSequenceNumber = staticSectionsCount + index + 1;
        ctrl.patchValue({ sequenceNumber: newSequenceNumber });
      });

      this.templateForm.markAsDirty();
      planSectionsArray.markAsDirty();

      this.cdr.markForCheck();
    }
  }

  private reorderCustomQuestions(event: CdkDragDrop<QuestionItem[]>) {
    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(
      this.customQuestions,
      event.previousIndex,
      event.currentIndex
    );
  }

  private reorderBehaviorSections(
    event: CdkDragDrop<(LookupItem | QuestionItem)[]>
  ): void {
    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(
      this.behvaiorSections,
      event.previousIndex,
      event.currentIndex
    );

    this.updateBehaviorSequenceNumbers();
    this.scheduleFormUpdate();
  }

  private updateQuestionApplicationObjectId(questionIndex: number): void {
    const movedQuestion = this.behvaiorSections[questionIndex];

    if (!movedQuestion || this.isSectionHeader(movedQuestion)) {
      return;
    }

    let sectionId = 0;
    for (let i = questionIndex - 1; i >= 0; i--) {
      const item = this.behvaiorSections[i];
      if (this.isSectionHeader(item)) {
        sectionId = item.sectionId;
        break;
      }
    }

    if (sectionId > 0) {
      movedQuestion.applicationObjectId = sectionId;
      movedQuestion.sectionId = sectionId;
    }
  }

  private isSectionHeader(item: BehaviorQuestionnaires): boolean {
    return item.questionId === 0;
  }

  private updateBehaviorSequenceNumbers(): void {
    let currentSectionSequence = 0;

    this.behvaiorSections.forEach((item, index) => {
      if (this.isSectionHeader(item)) {
        this.updateSectionHeader(item, ++currentSectionSequence);
      } else {
        this.updateQuestionItem(item, index);
      }
    });
  }

  private updateSectionHeader(
    item: BehaviorQuestionnaires,
    sequenceNumber: number
  ): void {
    item.behaviorTypeSequenceNumber = sequenceNumber;
    item.applicationObjectId = item.sectionId;
    item.questionSequenceNumber = 0;
    item.value =
      this.findOriginalSectionValue(item.sectionId) || item.value || '';
  }

  private updateQuestionItem(
    item: BehaviorQuestionnaires,
    currentIndex: number
  ): void {
    const actualSectionId = this.findActualSectionForQuestion(currentIndex);

    if (actualSectionId && actualSectionId !== item.sectionId) {
      item.sectionId = actualSectionId;
      item.applicationObjectId = actualSectionId;
    }

    const sectionHeader = this.findSectionHeader(item.sectionId);

    if (sectionHeader) {
      const questionSequence = this.calculateQuestionSequence(
        item.sectionId,
        currentIndex
      );

      item.behaviorTypeSequenceNumber =
        sectionHeader.behaviorTypeSequenceNumber;
      item.applicationObjectId = item.sectionId;
      item.questionSequenceNumber = questionSequence;
      item.value =
        (item.questionId ? this.findQuestionValue(item.questionId) : '') ||
        item.value ||
        '';
    }
  }

  private findActualSectionForQuestion(questionIndex: number): number {
    for (let i = questionIndex - 1; i >= 0; i--) {
      const item = this.behvaiorSections[i];
      if (this.isSectionHeader(item)) {
        return item.sectionId;
      }
    }
    return 0;
  }

  private findOriginalSectionValue(sectionId: number): string {
    return (
      this.originalBehaviorSections.find(section => section.id === sectionId)
        ?.value || ''
    );
  }

  private findSectionHeader(
    sectionId: number
  ): BehaviorQuestionnaires | undefined {
    return this.behvaiorSections.find(
      sectionItem =>
        this.isSectionHeader(sectionItem) && sectionItem.sectionId === sectionId
    );
  }

  private calculateQuestionSequence(
    sectionId: number,
    currentIndex: number
  ): number {
    return (
      this.behvaiorSections
        .slice(0, currentIndex)
        .filter(
          prevItem =>
            prevItem.sectionId === sectionId && !this.isSectionHeader(prevItem)
        ).length + 1
    );
  }

  private findQuestionValue(questionId: number): string | undefined {
    return this.customQuestions.find(q => q.id === questionId)?.customQuestion;
  }

  private scheduleFormUpdate(): void {
    setTimeout(() => {
      this.updateBehaviorQuestionnaires();
      this.cdr.markForCheck();
    });
  }

  private moveCustomQuestionToBehavior(
    event: CdkDragDrop<QuestionItem[]>
  ): void {
    const question = this.customQuestions[event.previousIndex];

    if (!question) {
      return;
    }

    if (!this.canMoveCustomQuestion(question)) {
      return;
    }

    const currentQuestionIds =
      this.templateForm.value.templateQuestionIds || [];

    const targetSectionIndex = event.currentIndex;
    const targetSection = this.behvaiorSections[targetSectionIndex];

    // Check if drop is between two section headers
    const isPrevSectionHeader =
      targetSectionIndex >= 0 &&
      this.isSectionHeader(this.behvaiorSections[targetSectionIndex]);
    const isNextSectionHeader =
      targetSectionIndex + 1 < this.behvaiorSections.length &&
      this.isSectionHeader(this.behvaiorSections[targetSectionIndex + 1]);

    let insertIndex = targetSectionIndex + 1;
    if (isPrevSectionHeader && isNextSectionHeader) {
      // Insert below the first section header
      insertIndex = targetSectionIndex + 1;
    }

    const questionsUnderTargetSection = this.behvaiorSections
      .slice(targetSectionIndex + 1)
      .filter(item => item.sectionId === targetSection.sectionId).length;
    if (!currentQuestionIds.includes(question.id)) {
      currentQuestionIds.push(question.id);
    }
    const behaviorItem: BehaviorQuestionnaires = {
      questionId: question.id,
      isSelected: true,
      sectionId: targetSection.sectionId,
      behaviorTypeSequenceNumber: targetSection.behaviorTypeSequenceNumber,
      questionSequenceNumber: questionsUnderTargetSection + 1,
      applicationObjectId: targetSection.sectionId,
      value: question.customQuestion,
    };

    this.templateForm.patchValue({ templateQuestionIds: currentQuestionIds });

    this.customQuestions.splice(event.previousIndex, 1);
    this.behvaiorSections.splice(insertIndex, 0, behaviorItem);

    setTimeout(() => {
      this.updateBehaviorQuestionnaires();
      this.cdr.markForCheck();
    });
  }

  private receiveCustomQuestionInBehavior(
    event: CdkDragDrop<(LookupItem | QuestionItem)[]>
  ) {
    if (event.previousContainer.id !== 'customQuestionsList') {
      return;
    }
    const question = event.previousContainer.data[
      event.previousIndex
    ] as QuestionItem;

    if (!question) {
      return;
    }

    if (!this.canMoveCustomQuestion(question)) {
      return;
    }

    const currentQuestionIds =
      this.templateForm.value.templateQuestionIds || [];

    const targetSectionIndex = event.currentIndex;
    const targetSection = this.behvaiorSections[targetSectionIndex];

    const questionsUnderTargetSection = this.behvaiorSections
      .slice(targetSectionIndex + 1)
      .filter(
        item => item.applicationObjectId === targetSection.sectionId
      ).length;

    const behaviorItem: BehaviorQuestionnaires = {
      questionId: question.id,
      isSelected: true,
      sectionId: targetSection.sectionId,
      behaviorTypeSequenceNumber: targetSection.behaviorTypeSequenceNumber,
      questionSequenceNumber: questionsUnderTargetSection + 1,
      applicationObjectId: targetSection.sectionId,
      value: question.customQuestion,
    };
    if (!currentQuestionIds.includes(question.id)) {
      currentQuestionIds.push(question.id);
    }
    this.templateForm.patchValue({ templateQuestionIds: currentQuestionIds });

    this.customQuestions.splice(event.previousIndex, 1);

    this.behvaiorSections.splice(targetSectionIndex + 1, 0, behaviorItem);

    setTimeout(() => {
      this.updateBehaviorQuestionnaires();
      this.cdr.markForCheck();
    });
  }

  private canMoveCustomQuestion(question: QuestionItem): boolean {
    const alreadyInBehavior = this.behvaiorSections.some(
      behavior => behavior.questionId === question.id
    );

    if (alreadyInBehavior) {
      return false;
    }

    return true;
  }

  private moveBehaviorQuestionToCustom(
    event: CdkDragDrop<(LookupItem | QuestionItem)[]>
  ): void {
    const draggedBehaviorItem = this.behvaiorSections[event.previousIndex];

    if (!draggedBehaviorItem || draggedBehaviorItem.questionId === 0) {
      return;
    }

    const questionExists = this.customQuestions.some(
      q => q.id === draggedBehaviorItem.questionId
    );

    if (questionExists) {
      return;
    }

    const originalQuestion: QuestionItem = {
      id: draggedBehaviorItem.questionId!,
      parentOrganizationId: this.orgId || 0,
      customQuestion: draggedBehaviorItem.value,
      isRequired: false,
      statusId: 1,
      questionForReportTypeId: 1,
      questionForReportType: 'Custom',
      sequenceNumber: this.customQuestions.length + 1,
      bipTemplates: [],
    };

    const currentQuestionIds =
      this.templateForm.value.templateQuestionIds || [];
    const updatedQuestionIds = currentQuestionIds.filter(
      (id: number) => id !== draggedBehaviorItem.questionId
    );
    this.templateForm.patchValue({ templateQuestionIds: updatedQuestionIds });

    this.behvaiorSections.splice(event.previousIndex, 1);

    this.customQuestions.splice(event.currentIndex, 0, originalQuestion);

    setTimeout(() => {
      this.updateBehaviorQuestionnaires();
      this.cdr.markForCheck();
    });
  }

  private updateBehaviorQuestionnaires() {
    const behaviorQuestionnaires = this.behvaiorSections.map(section => {
      let applicationObjectId: number;

      if (section.questionId === 0) {
        applicationObjectId = section.sectionId;
      } else {
        applicationObjectId = section.applicationObjectId || section.sectionId;
      }

      return {
        sectionId: section.sectionId,
        questionId: section.questionId,
        isSelected: section.questionId !== 0 ? true : section.isSelected,
        behaviorTypeSequenceNumber: section.behaviorTypeSequenceNumber,
        questionSequenceNumber: section.questionSequenceNumber,
        applicationObjectId: applicationObjectId,
        value: section.value,
      };
    });

    const applicationObjectIdsWithQuestions = new Set(
      behaviorQuestionnaires
        .filter(item => item.questionId !== 0)
        .map(item => item.applicationObjectId)
    );

    let filteredBehaviorQuestionnaires = behaviorQuestionnaires.filter(item => {
      if (
        item.questionId === 0 &&
        applicationObjectIdsWithQuestions.has(item.applicationObjectId)
      ) {
        return false;
      }
      return true;
    });

    const seenQuestionIds = new Set<number>();
    const duplicateIndices = new Set<number>();

    for (let i = filteredBehaviorQuestionnaires.length - 1; i >= 0; i--) {
      const item = filteredBehaviorQuestionnaires[i];
      if (item.questionId !== 0 && item.questionId !== undefined) {
        if (seenQuestionIds.has(item.questionId)) {
          duplicateIndices.add(i);
        } else {
          seenQuestionIds.add(item.questionId);
        }
      }
    }

    filteredBehaviorQuestionnaires = filteredBehaviorQuestionnaires.filter(
      (_, index) => !duplicateIndices.has(index)
    );

    const formArray = this.behaviorQuestionnaires;
    formArray.clear();

    filteredBehaviorQuestionnaires.forEach(item => {
      formArray.push(this.fb.group(item));
    });
  }

  private syncTemplateQuestionOrder() {
    const selected: number[] =
      this.templateForm.get('templateQuestionIds')?.value || [];

    if (!selected?.length) return;
    const ordered = [
      ...this.customQuestions
        .map(q => q.id)
        .filter(id => selected.includes(id)),
    ];

    this.templateForm.patchValue({ templateQuestionIds: ordered });
    console.log(this.templateData);

    if (this.templateData?.id) {
      this.loaderService.show();
      this.bipTemplateService
        .updateTemplateQuestionSequence(this.templateData.id, ordered)
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.drawerService.closeDrawer(DRAWER_IDS.ADD_TEMPLATE);
          },
          error: error => this.handleError(error),
        });
    }
  }

  getAllPlans(): void {
    if (!this.orgId) {
      return;
    }
    this.loaderService.show();
    this.manageSettingsService
      .getAllPlans(this.orgId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: response => {
          if (response.result) {
            this.planList = Array.isArray(response.result)
              ? response.result
              : response.result.items || [];

            setTimeout(() => {
              this.initializePlanSections();
            });
          }
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  private initializePlanSections(): void {
    const planSectionsArray = this.planSections;

    if (planSectionsArray.length > 0) {
      const existingPlanSections = planSectionsArray.controls.map(
        ctrl => ctrl.value
      );
      const existingPlanNamesSet = new Set(
        existingPlanSections.map(p => p.sectionName)
      );

      planSectionsArray.clear();

      const orderedPlanList: PlanPayload[] = [];
      const staticSectionsCount = this.templateSections.length;

      existingPlanSections.forEach(planSectionValue => {
        const plan = this.planList.find(
          p => p.name === planSectionValue.sectionName
        );
        if (plan) {
          orderedPlanList.push(plan);
          planSectionsArray.push(
            this.fb.group({
              id: planSectionValue.id || 0,
              sectionName: planSectionValue.sectionName,
              sequenceNumber: planSectionValue.sequenceNumber,
              isSelected: planSectionValue.isSelected,
            })
          );
        }
      });

      this.planList.forEach(plan => {
        if (!existingPlanNamesSet.has(plan.name)) {
          orderedPlanList.push(plan);
          planSectionsArray.push(
            this.fb.group({
              id: 0,
              sectionName: plan.name,
              sequenceNumber:
                staticSectionsCount + planSectionsArray.length + 1,
              isSelected: false,
            })
          );
        }
      });

      this.planList = orderedPlanList;
      return;
    }

    planSectionsArray.clear();

    const staticSectionsCount = this.templateSections.length;

    this.planList.forEach((plan, index) => {
      planSectionsArray.push(
        this.fb.group({
          id: 0,
          sectionName: plan.name,
          sequenceNumber: staticSectionsCount + index + 1,
          isSelected: false,
        })
      );
    });
  }

  togglePlanSelection(index: number, checked: boolean): void {
    this.planSections.at(index).patchValue({ isSelected: checked });
  }
}
